import React, { useState, InputHTMLAttributes, forwardRef } from 'react';
import * as styles from './DarkInput.css';

interface DarkInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    errorMessage?: string;
    placeholder?: string;
}

export const DarkInput = forwardRef<HTMLInputElement, DarkInputProps>(
    ({ label, errorMessage, type = 'text', placeholder = '', ...props }, ref) => {
        const [isFocused, setIsFocused] = useState(false);
        const [hasValue, setHasValue] = useState(Boolean(props.value));
        const [showPassword, setShowPassword] = useState(false);

        const isPassword = type === 'password';
        const inputType = isPassword && showPassword ? 'text' : type;

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true);
            props.onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false);
            props.onBlur?.(e);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setHasValue(Boolean(e.target.value));
            props.onChange?.(e);
        };

        const togglePasswordVisibility = () => {
            setShowPassword(!showPassword);
        };

        const showLabel = isFocused || hasValue;

        return (
            <div className={styles.container}>
                {label && (
                    <label className={showLabel ? styles.labelActive : styles.labelPlaceholder}>
                        {label}
                    </label>
                )}

                <div className={`${styles.inputContainer} ${errorMessage ? styles.error : ''}`}>
                    <input
                        {...props}
                        ref={ref}
                        type={inputType}
                        className={styles.input}
                        placeholder=""
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />

                    {isPassword && (
                        <button
                            type="button"
                            className={styles.toggleButton}
                            onClick={togglePasswordVisibility}
                            tabIndex={-1}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M2 12C2 12 5.63636 4.72729 12 4.72729C18.3636 4.72729 22 12 22 12C22 12 18.3636 19.2727 12 19.2727C5.63636 19.2727 2 12 2 12Z" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="1.81818" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                        </button>
                    )}
                </div>

                {errorMessage && (
                    <div className={styles.errorMessage}>{errorMessage}</div>
                )}
            </div>
        );
    }
);

DarkInput.displayName = 'DarkInput'; 